import React, { useEffect, useMemo, useRef, useState } from 'react';
import Header from '../../components/header';
import { useLanguage } from '../../helpers/language-context';
import { supabase } from '../../supabaseClient';


const Tender = () => {
  const { translations, language } = useLanguage();

  const [tenders, setTenders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dropdown + filter states
  const [pendingYear, setPendingYear] = useState('All Time');
  const [pendingSort, setPendingSort] = useState('Newest');
  const [appliedYear, setAppliedYear] = useState('All Time');
  const [appliedSort, setAppliedSort] = useState('Newest');

  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const yearDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      const { data, error } = await supabase
        .from('tenders')
        .select('*')
        .order('date', { ascending: false });
      if (!error) {
        setTenders(data || []);
      } else {
        console.error('Error fetching tenders:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let list = [...tenders];
    if (appliedYear !== 'All Time') {
      list = list.filter((item) => {
        if (!item.date) return false;
        const d = new Date(item.date);
        if (isNaN(d.getTime())) return false;
        return d.getFullYear().toString() === appliedYear;
      });
    }
    list.sort((a, b) => {
      const aD = a.date ? new Date(a.date) : new Date(0);
      const bD = b.date ? new Date(b.date) : new Date(0);
      return appliedSort === 'Newest' ? bD - aD : aD - bD;
    });
    setFiltered(list);
  }, [tenders, appliedYear, appliedSort]);

  const years = useMemo(() => {
    const ys = Array.from(
      new Set(
        tenders
          .map((t) => (t.date ? new Date(t.date).getFullYear() : null))
          .filter((y) => y !== null && !isNaN(y))
      )
    )
      .sort((a, b) => b - a)
      .map(String);
    return ['All Time', ...ys];
  }, [tenders]);

  const applyFilters = () => {
    setAppliedYear(pendingYear);
    setAppliedSort(pendingSort);
  };

  const resetFilters = () => {
    setPendingYear('All Time');
    setPendingSort('Newest');
    setAppliedYear('All Time');
    setAppliedSort('Newest');
  };

  const getLocalized = (row, keyBase) => {
    const enKey = `${keyBase}-en`;
    const msKey = `${keyBase}-ms`;
    return language === 'ms'
      ? row[msKey] || row[enKey] || ''
      : row[enKey] || row[msKey] || '';
  };

  const formatDate = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(language === 'ms' ? 'ms-MY' : 'en-MY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const ArrowRight = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  );

  const getStatus = (row) => {
    // 1. If manually closed, respect it.
    if (row.status === 'closed') return 'closed';

    // 2. Check date expiration
    if (row.date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tenderDate = new Date(row.date);
      tenderDate.setHours(0, 0, 0, 0);

      if (today > tenderDate) return 'closed';
    }

    // 3. If manual status is set (likely 'open'), return it.
    if (row.status) return row.status;

    // 4. Default
    return 'open';
  };

  return (
    <div>
      <Header title={translations?.contact?.tender?.title || 'Tender'} />

      <section>
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Year Dropdown */}
            <div className="relative" ref={yearDropdownRef}>
              <button
                onClick={() => setShowYearDropdown((v) => !v)}
                className="flex items-center justify-between gap-2 px-6 py-2.5 bg-white border border-[#28357B] rounded-full text-[#28357B] min-w-[140px] font-medium hover:bg-gray-50 transition-colors"
              >
                <span>{pendingYear}</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showYearDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showYearDropdown && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                  {years.map((yr) => (
                    <button
                      key={yr}
                      onClick={() => {
                        setPendingYear(yr);
                        setShowYearDropdown(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${pendingYear === yr ? 'text-[#28357B] font-bold bg-blue-50' : 'text-gray-700'}`}
                    >
                      {yr}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                onClick={() => setShowSortDropdown((v) => !v)}
                className="flex items-center justify-between gap-2 px-6 py-2.5 bg-white border border-[#28357B] rounded-full text-[#28357B] min-w-[140px] font-medium hover:bg-gray-50 transition-colors"
              >
                <span>{pendingSort}</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showSortDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showSortDropdown && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[140px] bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                  {['Newest', 'Oldest'].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        setPendingSort(opt);
                        setShowSortDropdown(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${pendingSort === opt ? 'text-[#28357B] font-bold bg-blue-50' : 'text-gray-700'}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Apply Filters */}
            <button
              onClick={applyFilters}
              className="px-8 py-2.5 bg-[#28357B] text-white rounded-full font-medium hover:bg-[#1e2a69] transition-colors"
            >
              {translations.button.filter}
            </button>

            {/* Reset */}
            <button
              onClick={resetFilters}
              className="px-6 py-2.5 bg-white border border-[#28357B] rounded-full text-[#28357B] font-medium hover:bg-gray-50 transition-colors"
            >
              {translations.button.resetFilter}
            </button>
          </div>
        </div>

        {/* Table Headings */}
        <div className="mt-10 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#28357B]"></div>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E0E0E0] text-[#999999] font-medium">
                  <th className="py-4 w-full md:w-1/5">Title</th>
                  <th className="py-4 hidden md:table-cell md:w-2/5">Description</th>
                  <th className="py-4 hidden md:table-cell w-1/5">Status</th>
                  <th className="py-4 hidden md:table-cell w-1/5">Date</th>
                  <th className="py-4 w-1/10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} className="border-b border-[#E0E0E0]">
                    <td className="py-8 align-top w-full block md:table-cell md:w-auto">
                      <div className="w-full flex justify-between md:block">
                        <div className="space-y-4 flex-1">
                          <p className="text-sm text-[#28357B] font-bold">
                            {getLocalized(row, 'title')}
                          </p>
                          {/* Mobile view for description and date */}
                          <p className="text-sm text-[#666666] md:hidden mt-2">
                            {getLocalized(row, 'description')}
                          </p>
                          <p className="text-xs text-gray-500 md:hidden mt-1">
                            {formatDate(row.date)}
                          </p>
                          <div className={`md:hidden inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatus(row) === 'closed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {getStatus(row) === 'closed' ? 'Closed' : 'Open'}
                          </div>
                        </div>
                        <div className="md:hidden flex flex-col items-end gap-2 pl-4">
                          {row.link ? (
                            <a
                              href={row.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-end gap-2 text-sm text-[#28357B] hover:underline whitespace-nowrap"
                            >
                              <span>Explore</span>
                              <ArrowRight />
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400 whitespace-nowrap">Explore</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell py-8 align-top text-[#666666] text-sm">
                      {getLocalized(row, 'description')}
                    </td>
                    <td className="hidden md:table-cell py-8 align-top">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatus(row) === 'closed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {getStatus(row) === 'closed' ? 'Closed' : 'Open'}
                      </span>
                    </td>
                    <td className="hidden md:table-cell py-8 align-top">
                      <span className="text-sm text-gray-500 hidden md:block mb-2">
                        {formatDate(row.date)}
                      </span>
                      <span className="text-xs text-gray-500 md:hidden">
                        {formatDate(row.date)}
                      </span>
                    </td>
                    <td className="hidden md:table-cell py-8 align-top text-right">
                      <div className="flex flex-col items-end gap-2">
                        {row.link ? (
                          <a
                            href={row.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-end gap-2 text-sm text-[#28357B] hover:underline"
                          >
                            <span>{translations.button.explore ?? 'Explore'}</span>
                            <ArrowRight />
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400">{translations.button.explore ?? 'Explore'}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && filtered.length === 0 && (
            <div className="py-10 text-center text-gray-500 border-b border-[#E0E0E0]">No tenders found</div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Tender;
